import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { EASE, scrollToId } from "./shared";

const HERO_IMG =
  "https://customer-assets-agu9un31.emergentagent.net/job_local-business-hub-148/artifacts/afiia7k4_homeHero1.png";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.05, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="inline-flex items-center gap-3 text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-[#a37d3f]"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-8 bg-[#c99a43]" /> Chester County, PA
          </motion.span>

          <h1
            data-testid="hero-heading"
            className="mt-6 font-serif font-normal tracking-tight text-[#16324a] text-4xl sm:text-5xl lg:text-6xl leading-[1.08]"
          >
            <MaskedLine delay={0.2}>More Leads for Your</MaskedLine>
            <MaskedLine delay={0.34}>
              <em className="text-[#c99a43]">Chester County</em>
            </MaskedLine>
            <MaskedLine delay={0.48}>Business.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-6 text-base sm:text-lg text-[#334e68] leading-relaxed max-w-xl"
          >
            We optimize your Google Business Profile, build a mobile-first website that turns
            visitors into leads, and keep your Google search rankings working for you so West
            Chester and Kennett Square customers find you first.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-plan"
              onClick={() => scrollToId("pricing")}
              className="group inline-flex items-center gap-2 bg-[#16324a] text-[#faf7f2] font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full hover:bg-[#c99a43] hover:text-[#16324a] transition-colors duration-300"
            >
              See the Plan
              <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              data-testid="hero-cta-call"
              href="tel:6104534448"
              className="inline-flex items-center gap-2 border border-[#16324a]/25 text-[#16324a] font-semibold text-sm sm:text-base px-7 py-3.5 rounded-full hover:border-[#c99a43] hover:text-[#c99a43] transition-colors duration-300"
            >
              <Phone size={16} /> (610) 453-4448
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.05 }}
            className="mt-6 text-sm text-[#627d98]"
          >
            $399 flat build &middot; $75/mo hosting &amp; updates &middot; Serving all of Chester County, PA
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
          className="hidden lg:block lg:col-span-5 relative"
        >
          <div className="absolute -top-4 -right-4 w-full h-full rounded-2xl border border-[#c99a43]/50" />
          <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-square max-h-[540px] w-full">
            <motion.img
              src={HERO_IMG}
              alt="Downtown West Chester PA - get found online with Chesco Local Presence"
              style={{ y: imgY }}
              className="w-full h-[120%] object-cover"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#16324a]/40 via-transparent to-transparent" />
          </div>
          <div className="float-slow absolute -bottom-5 -left-4 sm:-left-8 bg-[#16324a] text-[#faf7f2] rounded-xl px-5 py-4 shadow-xl">
            <p className="font-serif italic text-[#e8c78f] text-2xl leading-none">$399</p>
            <p className="text-xs tracking-wide mt-1 text-[#cdd8de]">flat website build</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
